import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

import { loginFormSchema, type LoginFormData } from '@/lib/schema/authSchema';
import { loginRequest } from '@/services/auth/authService';
import { useAuthActions } from '@/store/authStore';
import { useTranslation } from '@/store/languageStore';
import AuthenticationCode from '@/components/auth/AuthenticationCode';
import api from '@/services/api/client';
import { useSsoDomainLogin } from '@/hooks/useSsoDomainLogin';
import { buildSsoInitiateUrl, type SsoDiscoverInfo } from '@/utils/ssoRedirect';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setLoginDetails } = useAuthActions();
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [requiresEmailVerification, setRequiresEmailVerification] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [resendingVerification, setResendingVerification] = useState(false);
  const [signupOrgLinked, setSignupOrgLinked] = useState(false);
  const [preferPassword, setPreferPassword] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setError,
    setValue,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const emailValue = watch('email') || '';
  const redirectTo = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname || '/';
  const { sso, checking, cameBack, startSso } = useSsoDomainLogin(emailValue, {
    enabled: !requiresEmailVerification,
    preferPassword,
    redirectPath: redirectTo,
  });

  useEffect(() => {
    setPreferPassword(false);
  }, [emailValue]);

  useEffect(() => {
    const state = location.state as {
      email?: string;
      requiresVerification?: boolean;
      message?: string;
      organizationLinkedByDomain?: boolean;
    } | null;
    if (state?.requiresVerification) {
      setRequiresEmailVerification(true);
      setVerificationEmail(state.email || '');
      setSignupOrgLinked(state.organizationLinkedByDomain === true);
      if (state.email) {
        setValue('email', state.email);
      }
    }
  }, [location.state, setValue]);

  const goToSso = (info: SsoDiscoverInfo, email: string) => {
    startSso(info, email);
  };

  const onSubmit = async (form: LoginFormData) => {
    const trimmed = form.email.trim();

    if (!preferPassword) {
      if (sso) {
        goToSso(sso, trimmed);
        return;
      }
      setIsLoading(true);
      try {
        const res = await api.get('/api/auth/federation/discover', { params: { email: trimmed } });
        const data = res.data?.data;
        const method = data?.authMethod;
        if (data?.ssoRequired && data?.organizationId && (method === 'OIDC' || method === 'SAML2')) {
          goToSso(
            {
              organizationId: String(data.organizationId),
              organizationName: data.organizationName,
              authMethod: method,
            },
            trimmed
          );
          return;
        }
      } catch {
        // Fail-open mot lösenordsinloggning
      } finally {
        setIsLoading(false);
      }
    }

    if (!form.password || form.password.length < 6) {
      setError('password', { type: 'manual', message: t('login.passwordRequired') });
      return;
    }

    setIsLoading(true);

    try {
      const response = await loginRequest({
        email: form.email,
        password: form.password,
        preferPassword: preferPassword || undefined,
      });

      if (response.status === 200 && response.data) {
        const responseData = response.data;

        if (responseData.success) {
          const data = responseData.data as any;

          if (data?.accessToken) {
            setLoginDetails({
              accessToken: data.accessToken,
              refreshToken: data.refreshToken,
              user: data.user,
            });

            if (data?.authMethodChanged) {
              toast.success(responseData.message || t('auth.authMethodChanged') || 'Autentiseringsmetod har ändrats');
            } else {
              toast.success(t('login.welcomeMessage'));
            }
            navigate('/');
          } else if (data?.requiresEmailVerification) {
            setRequiresEmailVerification(true);
            setVerificationEmail(data.email || '');
            toast.error(responseData.error || 'Du måste verifiera din e-postadress innan du kan logga in.');
          } else if (data?.email) {
            setUserEmail(data.email);
            setIsVerified(true);
            if (data?.authMethodChanged) {
              toast.success(responseData.message || t('auth.authMethodChanged') || 'Autentiseringsmetod har ändrats');
            } else {
              toast.success(responseData.message || t('auth.otpSent') || 'Verifieringskod skickad');
            }
          } else if (data?.requiresSaml || data?.requiresOidc) {
            const orgId = data?.organizationId;
            if (orgId) {
              window.location.replace(
                buildSsoInitiateUrl({
                  organizationId: String(orgId),
                  authMethod: data.requiresSaml ? 'SAML2' : 'OIDC',
                  email: trimmed,
                  redirect: redirectTo,
                })
              );
            } else {
              toast.error(t('auth.samlRequired') || 'SSO krävs för denna organisation men organisation kunde inte bestämmas.');
            }
          }
        }
      }
    } catch (error: any) {
      if (error.response?.data?.requiresEmailVerification) {
        setRequiresEmailVerification(true);
        setVerificationEmail(error.response?.data?.email || form.email);
        toast.error(error.response?.data?.error || 'Du måste verifiera din e-postadress innan du kan logga in.');
      } else {
        const errorMessage =
          error.response?.data?.error || error.response?.data?.message || t('login.loginError');

        toast.error(errorMessage);

        setError('password', {
          type: 'manual',
          message: errorMessage,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!verificationEmail) return;

    try {
      setResendingVerification(true);
      await api.post('/api/auth/resend-verification-email', { email: verificationEmail });
      toast.success(t('login.resendVerificationSuccess'));
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('login.resendVerificationError'));
    } finally {
      setResendingVerification(false);
    }
  };

  if (isVerified) {
    return (
      <AuthenticationCode
        setIsVerified={setIsVerified}
        email={userEmail}
        type="LOGIN"
      />
    );
  }

  const showPasswordField = preferPassword || (!sso && !checking);
  const orgLabel = sso?.organizationName || t('auth.ssoLogin');
  const ssoBusy = Boolean(sso && !preferPassword && !cameBack);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-100">
      <div className="max-w-md w-full bg-white dark:bg-white rounded-lg shadow-md p-8">
        <h2 className="text-3xl font-bold text-center mb-8 text-gray-800 dark:text-gray-800">{t('login.title')}</h2>

        {requiresEmailVerification && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg dark:bg-yellow-950/30 dark:border-yellow-800">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                  {t('login.verifyEmailTitle')}
                </h3>
                <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-300 space-y-2">
                  <p>
                    {verificationEmail
                      ? t('login.verifyEmailBodyWithEmail').replace('{email}', verificationEmail)
                      : t('login.verifyEmailBody')}
                  </p>
                  <p className="text-yellow-800/90 dark:text-yellow-200/90">
                    {signupOrgLinked ? t('login.verifyOrgLinkedNote') : t('login.verifyManualOrgNote')}
                  </p>
                  {verificationEmail && (
                    <button
                      type="button"
                      onClick={handleResendVerification}
                      disabled={resendingVerification}
                      className="mt-1 text-sm font-medium text-yellow-800 dark:text-yellow-200 hover:text-yellow-900 dark:hover:text-yellow-100 underline disabled:opacity-50"
                    >
                      {resendingVerification ? t('login.resendingVerification') : t('login.resendVerification')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-gray-700 dark:text-gray-700 text-sm font-bold mb-2">
              {t('login.emailLabel')} <span className="text-red-500">*</span>
            </label>
            <input
              id="email"
              type="email"
              autoComplete="username"
              {...register('email')}
              className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-white text-gray-900 dark:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.email ? 'border-red-500' : 'border-gray-300 dark:border-gray-300'
              }`}
              placeholder={t('login.emailPlaceholder')}
              disabled={isLoading || ssoBusy}
            />
            {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email.message}</p>}
            {ssoBusy && (
              <p className="mt-2 text-sm text-blue-700">{t('auth.ssoRedirect')}</p>
            )}
            {sso && cameBack && !preferPassword && (
              <p className="mt-2 text-sm text-gray-600">
                {t('auth.ssoContinue').replace('{org}', orgLabel)}
              </p>
            )}
          </div>

          {showPasswordField && (
            <div>
              <label htmlFor="password" className="block text-gray-700 dark:text-gray-700 text-sm font-bold mb-2">
                {t('login.passwordLabel')} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  {...register('password')}
                  className={`w-full px-3 py-2 pr-10 border rounded-md bg-white dark:bg-white text-gray-900 dark:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.password ? 'border-red-500' : 'border-gray-300 dark:border-gray-300'
                  }`}
                  placeholder={t('login.passwordPlaceholder')}
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-textTertiary dark:text-textTertiary hover:text-gray-700 dark:hover:text-gray-700"
                  disabled={isLoading}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-500">{errors.password.message}</p>
              )}
              <div className="mt-2 text-right">
                <Link to="/reset-password" className="text-sm text-blue-600 hover:text-blue-800">
                  {t('login.forgotPassword')}
                </Link>
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading || checking || ssoBusy}
            className={`w-full bg-blue-500 text-white font-bold py-2 px-4 rounded-md hover:bg-blue-600 transition duration-200 ${
              isLoading || checking || ssoBusy ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {sso && !preferPassword
              ? t('auth.useSsoInstead')
              : isLoading
                ? t('login.loggingIn')
                : t('login.loginButton')}
          </button>

          {sso && !preferPassword && (
            <button
              type="button"
              onClick={() => setPreferPassword(true)}
              className="w-full text-sm text-gray-500 hover:text-gray-700"
            >
              {t('auth.loginWithPassword')}
            </button>
          )}
        </form>

        <div className="mt-6 text-center text-sm text-textSecondary dark:text-textSecondary">
          {t('login.noAccount')}{' '}
          <Link to="/signup" className="text-blue-600 hover:text-blue-800 font-semibold">
            {t('login.createAccount')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
